import { offer_has_pending_cerebro_phase, offer_uses_cerebro_handoff } from '../../../helpers/preoffer_handoff';

const preoffer_sync_item = (offerItem = {}) => {
    return offerItem?.workflow?.cerebroSync?.preoffer || {};
};

const normalized_value = (value = '') => `${value || ''}`.trim().toLowerCase();

export const offer_overview_path = (offerId = '', routeLanguage = 'el') => {
    const normalizedOfferId = `${offerId || ''}`.trim();

    if (!normalizedOfferId.length) {
        return '';
    }

    return `/${routeLanguage || 'el'}/offer/${encodeURIComponent(normalizedOfferId)}?viewOnly=1`;
};

export const offer_review_stage = (offerItem = {}) => {
    const workflowVersion = Number(
        offerItem?.workflow?.version
        || offerItem?.setupData?.preofferWorkflowVersion
        || 0
    );
    const workflowTurn = normalized_value(
        offerItem?.workflow?.turn
        || offerItem?.typesData?.preofferTurn
        || offerItem?.setupData?.preofferTurn
    );

    if (workflowVersion !== 2) {
        return '';
    }

    if (workflowTurn === 'preparer') {
        return 'uncompleted';
    }

    if (workflowTurn === 'creator') {
        return 'reviewed';
    }

    if (workflowTurn === 'done') {
        return 'created';
    }

    return '';
};

export const offer_review_action = (offerItem = {}) => {
    if (offer_uses_cerebro_handoff(offerItem)) {
        return '';
    }

    const reviewStage = offer_review_stage(offerItem);

    if (reviewStage === 'uncompleted') {
        return 'Review';
    }

    if (reviewStage === 'reviewed') {
        return 'Create';
    }

    return '';
};

export const offer_card_shows_visits = (offerItem = {}, boardStatus = '') => {
    const reviewStage = offer_review_stage(offerItem);

    if (reviewStage.length) {
        return reviewStage === 'created';
    }

    return ![
        'help',
        'requested',
        'preoffer',
        'reviewed',
        'draft',
        'ready_for_review',
        'uncompleted'
    ].includes(normalized_value(boardStatus));
};

export const offer_should_open_as_draft = (offerItem = {}, boardStatus = '', isLegacyBoard = false) => {
    if (isLegacyBoard || offer_uses_cerebro_handoff(offerItem)) {
        return false;
    }

    const workflowTurn = normalized_value(
        offerItem?.workflow?.turn
        || offerItem?.typesData?.preofferTurn
        || offerItem?.setupData?.preofferTurn
    );
    const origin = normalized_value(offerItem?.origin || offerItem?.workflow?.origin);

    if (workflowTurn === 'done') {
        return false;
    }

    return ['help', 'request'].includes(origin)
        || ['help', 'requested', 'preoffer', 'reviewed', 'draft', 'ready_for_review'].includes(normalized_value(boardStatus));
};

const offer_created_sort_time = (offerItem = {}) => {
    const createdAt = offerItem?.createdAt
        || offerItem?.offerCreatedAt
        || offerItem?.created_at
        || offerItem?.typesData?.createdAt
        || offerItem?.setupData?.createdAt
        || '';
    const createdTime = new Date(createdAt || 0).getTime();

    if (Number.isNaN(createdTime)) {
        return 0;
    }

    return createdTime;
};

export const sort_offers_by_created_at = (offers = []) => {
    return [...offers].sort((leftItem, rightItem) => {
        return offer_created_sort_time(rightItem) - offer_created_sort_time(leftItem);
    });
};

const offer_actor_profile = (actor = {}) => {
    const actorItem = actor && typeof actor === 'object' ? actor : {};
    const fullName = `${actorItem.firstname || actorItem.firstName || ''} ${actorItem.lastname || actorItem.lastName || ''}`.trim();

    return {
        id: `${actorItem.userId || actorItem.memberId || actorItem.subId || actorItem.id || actorItem._id || ''}`.trim(),
        label: `${fullName || actorItem.name || actorItem.email || actorItem.username || ''}`.trim(),
        email: `${actorItem.email || ''}`.trim()
    };
};

export const offer_creator_profile = (offerItem = {}) => {
    const createdBy = offerItem?.createdBy && typeof offerItem.createdBy === 'object'
        ? offerItem.createdBy
        : {};
    const requestedBy = offerItem?.workflow?.requestedBy && typeof offerItem.workflow.requestedBy === 'object'
        ? offerItem.workflow.requestedBy
        : {};

    return offer_actor_profile({
        ...createdBy,
        ...requestedBy
    });
};

export const offer_reviewer_profile = (offerItem = {}) => {
    const workflow = offerItem?.workflow || {};
    const candidates = offer_review_stage(offerItem) === 'uncompleted'
        ? [workflow.reviewer, workflow.assignedTeamLead, offerItem?.setupData?.preofferAssignedTeamLead]
        : [workflow.preparedBy, workflow.estimateSubmittedBy, offerItem?.setupData?.preofferEstimateSubmittedBy];

    return candidates.map(offer_actor_profile).find((profile) => profile.label) || offer_actor_profile();
};

export const offer_completed_creator_profile = (offerItem = {}) => {
    const workflow = offerItem?.workflow || {};

    return offer_actor_profile(offer_review_stage(offerItem) === 'reviewed'
        ? workflow.creatorMember
        : workflow.completedBy);
};

export const preoffer_sync_status = (offerItem = {}) => {
    const syncItem = preoffer_sync_item(offerItem);
    const failedResult = Array.isArray(syncItem.results)
        ? syncItem.results.find((resultItem) => resultItem?.synced !== true && resultItem?.skipped !== true)
        : null;

    if (failedResult) {
        return syncItem.partial === true ? 'partial' : 'failed';
    }

    if (syncItem.partial === true) {
        return 'partial';
    }

    if (syncItem.synced === true) {
        return 'synced';
    }

    if (syncItem.skipped === true) {
        return 'skipped';
    }

    return 'missing';
};

export const preoffer_sync_error = (offerItem = {}) => {
    const syncItem = preoffer_sync_item(offerItem);
    const failedResult = Array.isArray(syncItem.results)
        ? syncItem.results.find((resultItem) => resultItem?.error)
        : null;

    return `${failedResult?.error || ''}`
        .trim()
        .replace(/\bniffler\b/gi, 'Leads')
        .replace(/\bcerebro\b/gi, 'Tasks')
        .replace(/\bgodfather\b/gi, 'Offers');
};

export const cerebro_connection_status = (offerItem = {}) => {
    const connection = offerItem?.workflow?.cerebroConnection || {};
    const hasClient = Boolean(connection.clientId || connection.mongoId || offerItem?.connection?.links?.cerebro?.clientId);
    const status = `${connection.status || ''}`.trim().toLowerCase();
    const statusLookup = {
        resolving: 'pending',
        pending: 'pending',
        failed: 'failed',
        synced: hasClient ? 'linked' : 'not linked'
    };

    return hasClient ? 'linked' : statusLookup[status] || 'not linked';
};

export const preoffer_sync_can_retry = (offerItem = {}, boardStatus = '', isLegacyBoard = false) => {
    if (isLegacyBoard) {
        return false;
    }

    if (!offer_has_pending_cerebro_phase(offerItem) && !['help', 'requested', 'preoffer'].includes(boardStatus)) {
        return false;
    }

    return ['missing', 'failed', 'partial'].includes(preoffer_sync_status(offerItem));
};
